import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { User } from '../services/userService';
import { UserPermissions } from '../types/permission';
import { hasPermissionIn } from '../utils/permissions';
import { useAutoLogout } from '../hooks/useAutoLogout';
import settingsService from '../services/settingsService';
import { setSentryUser } from '../utils/sentry';
import { setUserDateFormat, setUserTimezone } from '../utils/dateUtils';

// ประเภทข้อมูลบริบทการยืนยันตัวตน
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** true เฉพาะช่วง bootstrap auth ครั้งแรกเท่านั้น — route guard ควรใช้ตัวนี้ ไม่ใช่ isLoading
   *  (isLoading เป็น true ระหว่าง login() ด้วย ซึ่งจะทำให้ guard ถอดหน้า login ทิ้งกลางคัน) */
  isInitializing: boolean;
  error: string | null;
  permissions: UserPermissions;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  hasPermission: (permission: string | string[]) => boolean;
}

// สร้างบริบทการยืนยันตัวตน
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// คุณสมบัติของ Provider
interface AuthProviderProps {
  children: ReactNode;
}

// ผู้ให้บริการบริบทการยืนยันตัวตน
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // BUG-AUTH-REMOUNT FIX: การ gate children ด้านล่างตั้งใจกันเฉพาะ "ตอน bootstrap ครั้งแรก"
  // แต่เดิมผูกกับ isLoading ซึ่ง login() ก็ตั้งด้วย → ทุกครั้งที่กดเข้าสู่ระบบ children ถูก unmount
  // ทั้งต้นไม้แล้ว mount ใหม่ → LoginForm เสีย state (username ที่พิมพ์ไว้หาย, focus เด้งกลับช่องแรก)
  // แยก flag นี้ออกมาเพื่อให้ gate ครอบเฉพาะ bootstrap จริงๆ
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions>({});
  const navigate = useNavigate();

  // Helper function สำหรับ check nested permission เช่น "dashboard.view"
  // P-S616-1: ตรรกะย้ายไป utils/permissions.ts (pure) เพื่อให้ LoginForm ใช้ชุดเดียวกันได้
  // ตอนตัดสินปลายทางหลัง login ซึ่งยังอ่าน context state ที่อัพเดตแล้วไม่ได้
  const checkNestedPermission = useCallback(
    (permissionKey: string): boolean => hasPermissionIn(permissions, permissionKey),
    [permissions]
  );

  // ฟังก์ชันตรวจสอบสิทธิ์
  const hasPermission = useCallback((permission: string | string[]): boolean => {
    // Super Admin/Super User มีสิทธิ์ทั้งหมด
    // G-02: bypass model เดียวกันทั้ง 4 ชั้น (ProtectedRoute, hasPermission ตัวนี้,
    // AuthGuard, buildMenuAccessChecker) — ตรงกับ backend core/permissions.py ที่ bypass บน is_superuser
    if (user?.is_superadmin || user?.is_superuser || permissions.all === true) {
      return true;
    }

    // ถ้าส่งมาเป็น array ให้เช็คว่ามีสิทธิ์อย่างน้อย 1 อัน
    if (Array.isArray(permission)) {
      const result = permission.some(p => checkNestedPermission(p));
      return result;
    }

    // ถ้าส่งมาเป็น string ให้เช็คด้วย nested logic
    const result = checkNestedPermission(permission);
    return result;
  }, [user, permissions, checkNestedPermission]);

  // BUG-S2004-001: authService.validateToken() sync permissions/role จาก /auth/me แล้วยิง event
  // → อัพเดท state ให้ sidebar/ProtectedRoute/hasPermission เห็นสิทธิ์ล่าสุดทันที (ไม่ต้อง login ใหม่)
  useEffect(() => {
    const onPermsUpdated = () => {
      setPermissions(authService.getPermissions());
      const refreshed = authService.getCurrentUser();
      if (refreshed) { setUser(refreshed); }
    };
    window.addEventListener('auth:permissions-updated', onPermsUpdated);
    return () => window.removeEventListener('auth:permissions-updated', onPermsUpdated);
  }, []);

  // ตรวจสอบสถานะการยืนยันตัวตนเมื่อโหลดแอป
  useEffect(() => {
    let hasTriedAutoLogin = false;

    const checkAuth = async () => {
      try {
        console.log('🔍 Starting auth check in environment:', import.meta.env.MODE);

        // ตรวจสอบ token ที่มีอยู่ก่อน
        if (authService.isAuthenticated()) {
          const userData = authService.getCurrentUser();
          console.log('🔐 Found existing auth data:', { hasUser: !!userData });

          if (userData) {
            // มี token (หรือ refresh token) + user data → ใช้ต่อเลย
            // ไม่ต้องเรียก server validation ทันที — interceptor จะจัดการ refresh ได้ถ้า token หมดอายุ
            console.log('🚀 Using existing auth data (refresh token will handle expiry)');
            setUser(userData); setSentryUser(userData);
            setIsAuthenticated(true);
            setPermissions(authService.getPermissions());
            setError(null);

            // Background validation: ตรวจสอบกับ server แบบ non-blocking
            authService.validateToken().then((isValid) => {
              if (!isValid) {
                // ลอง refresh ก่อน logout
                const refreshToken = localStorage.getItem('refresh_token');
                if (!refreshToken) {
                  console.log('🔐 Token invalid and no refresh token, clearing auth');
                  authService.logout();
                  setIsAuthenticated(false);
                  setUser(null); setSentryUser(null);
                  setPermissions({});
                  navigate('/login');
                }
                // ถ้ามี refresh token → interceptor จะจัดการ
              }
            }).catch(() => {
              // Network error → keep existing auth
              console.warn('Background validation failed, keeping existing auth');
            });
          } else {
            // มี token แต่ไม่มี user data → ลอง validate
            try {
              const isValid = await authService.validateToken();
              if (!isValid) {
                console.log('🔐 Token validation failed, clearing auth');
                authService.logout();
                setIsAuthenticated(false);
                setUser(null); setSentryUser(null);
                setPermissions({});
                if (!hasTriedAutoLogin) {
                  await tryAutoLogin();
                }
              }
            } catch {
              authService.logout();
              setIsAuthenticated(false);
              setUser(null); setSentryUser(null);
              setPermissions({});
              if (!hasTriedAutoLogin) {
                await tryAutoLogin();
              }
            }
          }
        } else {
          // ไม่มี token - ลอง auto-login ใน development เฉพาะครั้งแรก
          console.log('🔐 No existing auth found');
          if (!hasTriedAutoLogin) {
            await tryAutoLogin();
          }
        }
      } catch (error: any) {
        console.error('🚨 Auth check error:', error);

        const userData = authService.getCurrentUser();
        const refreshToken = localStorage.getItem('refresh_token');

        if (userData && refreshToken) {
          // มี user data + refresh token → keep auth (interceptor จะจัดการ)
          console.log('🚀 Keeping existing auth despite error (has refresh token)');
          setUser(userData); setSentryUser(userData);
          setIsAuthenticated(true);
          setPermissions(authService.getPermissions());
          setError(null);
        } else {
          setIsAuthenticated(false);
          setUser(null); setSentryUser(null);
          setPermissions({});
          setError(null);

          if (!hasTriedAutoLogin) {
            await tryAutoLogin();
          }
        }
      } finally {
        setIsLoading(false);
        setIsInitializing(false);
      }
    };

    // ฟังก์ชันสำหรับ auto-login ใน development
    const tryAutoLogin = async () => {
      // ป้องกันการ auto-login ซ้ำ
      if (hasTriedAutoLogin) {
        return;
      }
      hasTriedAutoLogin = true;

      // ตรวจสอบว่ามีการ clear auth มาก่อนหรือไม่ (จาก 401 error)
      const authCleared = sessionStorage.getItem('auth_cleared');
      if (authCleared) {
        sessionStorage.removeItem('auth_cleared');
        console.log('🔐 Auth was cleared due to 401 error, skipping auto-login');
        return;
      }

      // ทำงานเฉพาะใน development environment และเมื่อเปิดใช้ auto-login
      if (import.meta.env.MODE === 'development' && import.meta.env.VITE_AUTO_LOGIN === 'true') {
        try {
          const username = import.meta.env.VITE_DEFAULT_USERNAME || '';
          const password = import.meta.env.VITE_DEFAULT_PASSWORD || '';

          console.log('🔧 Development mode: Attempting auto-login with', username + '...');
          await login(username, password);
          console.log('✅ Auto-login successful');
        } catch (error) {
          console.log('❌ Auto-login failed, user needs to login manually:', error);
          setIsAuthenticated(false);
          setUser(null); setSentryUser(null);
          setPermissions({});
          setError(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null); setSentryUser(null);
        setPermissions({});
        setError(null);
      }
    };

    checkAuth();
  }, []);

  // Multi-tab sync: ฟัง storage event เพื่อ sync logout ข้ามแท็บ
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // ถ้า access_token ถูกลบ (logout จาก tab อื่น) → clear state + redirect
      if (e.key === 'access_token' && e.newValue === null && isAuthenticated) {
        console.log('🔄 Multi-tab sync: Detected logout from another tab');
        setUser(null); setSentryUser(null);
        setIsAuthenticated(false);
        setPermissions({});
        navigate('/login');
      }

      // ถ้า access_token ถูกเปลี่ยน (refresh จาก tab อื่น) → อัพเดท user data
      if (e.key === 'access_token' && e.newValue && e.oldValue && e.newValue !== e.oldValue) {
        console.log('🔄 Multi-tab sync: Token refreshed from another tab');
        // Token ใหม่จะถูกใช้อัตโนมัติจาก localStorage ใน interceptor
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isAuthenticated, navigate]);

  // ฟังก์ชันเข้าสู่ระบบ
  const login = async (username: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // เรียกใช้ authService เพื่อเข้าสู่ระบบ
      await authService.login({ username, password });

      // ดึงข้อมูลผู้ใช้จาก localStorage
      const userData = authService.getCurrentUser();
      // S1513 OBS-S1513-002 FIX: was console.log(userData) — leaked full user object
      // (incl. potentially sensitive fields) to browser console on every login.
      // Gated to DEV-only — production users shouldn't see this.
      if (import.meta.env.DEV) {
        console.log('Retrieved user data after login:', userData);
      }

      if (userData) {
        setUser(userData); setSentryUser(userData);
        setIsAuthenticated(true);
        setPermissions(authService.getPermissions());
        setError(null);
        // S1513 OBS-S1513-002 FIX: also gate the permissions log (lists user's capabilities)
        if (import.meta.env.DEV) {
          console.log('🔐 Login successful, permissions loaded:', authService.getPermissions());
        }
      } else {
        throw new Error('ไม่พบข้อมูลผู้ใช้หลังจากเข้าสู่ระบบ');
      }
    } catch (error: any) {
      console.error('Login error in context:', error);

      // อธิบายข้อผิดพลาดให้ชัดเจน
      let errorMessage = 'ไม่สามารถเข้าสู่ระบบได้';

      if (error.message?.includes('Network Error')) {
        errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่อเครือข่ายและลองอีกครั้ง';
      } else if (error.response?.status === 401 || error.response?.status === 422) {
        errorMessage = 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง';
      } else if (error.response?.status === 429) {
        errorMessage = 'คุณพยายามเข้าสู่ระบบบ่อยเกินไป กรุณารอสักครู่แล้วลองใหม่อีกครั้ง';
      } else if (error.response?.status === 403) {
        // BUG-S348-ROLEDISABLED-MSG FIX: backend แยกสาเหตุมาให้แล้ว (ROLE_DISABLED = บทบาทถูกปิด
        // ทั้งบทบาท · USER_INACTIVE = บัญชีนี้ถูกปิด) — ใช้ข้อความจริงจาก envelope ก่อน
        // เพราะสองกรณีนี้ผู้ดูแลต้องไปแก้คนละที่ · ถ้าไม่มีค่อย fallback ข้อความรวมเดิม
        errorMessage = error.response?.data?.error?.details?.message
          || error.response?.data?.error?.user_message
          || 'บัญชีของคุณถูกระงับการใช้งาน กรุณาติดต่อผู้ดูแลระบบ';
      } else if (error.response?.status === 404) {
        errorMessage = 'ไม่สามารถเชื่อมต่อกับระบบได้ กรุณาลองใหม่อีกครั้ง';
      } else if (error.response?.status === 500) {
        errorMessage = 'เซิร์ฟเวอร์เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้งในภายหลัง';
      } else if (error.response?.status === 502 || error.response?.status === 503) {
        errorMessage = 'ระบบกำลังปรับปรุง กรุณาลองใหม่อีกครั้งในภายหลัง';
      } else if (error.response?.data?.detail && typeof error.response.data.detail === 'string') {
        errorMessage = error.response.data.detail;
      } else if (error.message?.includes('timeout')) {
        errorMessage = 'การเชื่อมต่อใช้เวลานานเกินไป กรุณาลองใหม่อีกครั้ง';
      } else {
        errorMessage = 'ไม่สามารถเข้าสู่ระบบได้ กรุณาลองใหม่อีกครั้ง';
      }

      setError(errorMessage);
      setIsAuthenticated(false);
      setUser(null); setSentryUser(null);
      setPermissions({});
    } finally {
      setIsLoading(false);
    }
  };

  // ฟังก์ชันออกจากระบบ
  const logout = () => {
    authService.logout();
    setUser(null); setSentryUser(null);
    setIsAuthenticated(false);
    setPermissions({});
    navigate('/login');
  };

  // ล้างข้อผิดพลาด
  const clearError = () => {
    setError(null);
  };

  // BUG-S1043-003 fix: enforce systemSettings.autoLogout (saved-but-not-consumed).
  // Hook is a no-op when !isAuthenticated; reads setting on auth state change.
  useAutoLogout(isAuthenticated, logout);

  // BUG-S1043-001 + BUG-S1043-002 fix: populate dateFormat + timezone cache on auth so
  // formatDate / formatDateTime honor user preference (FINDING-S980-A — saved-but-not-consumed).
  // Defaults are backward-compatible if fetch fails or values are absent.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await settingsService.getGeneralSettings();
        if (cancelled) return;
        setUserDateFormat(s?.displaySettings?.dateFormat ?? null);
        setUserTimezone(s?.displaySettings?.timezone ?? 'Asia/Bangkok');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.debug('AuthContext: could not load display settings:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // ค่าที่ส่งให้ Context
  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    isInitializing,
    error,
    permissions,
    login,
    logout,
    clearError,
    hasPermission
  };

  // Block rendering children until auth initialization is complete.
  // This prevents NotificationProvider, MaintenanceAlertProvider, and all
  // page-level useQuery hooks from firing API requests before the token is
  // available in localStorage — eliminating the ~49 401 errors on first load.
  // ผูกกับ isInitializing (ไม่ใช่ isLoading) — ดูคอมเมนต์ BUG-AUTH-REMOUNT ด้านบน
  if (isInitializing) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Safe default fallback (BUG-S1144-001 FIX — OBS-S1142-001):
// Returned when AuthContext is unavailable due to HMR module re-evaluation race
// or rare lazy-load timing edge case. Prevents ErrorBoundary trigger that would
// redirect user to /error page on transient first-navigation crashes. Treats
// user as unauthenticated so ProtectedRoute naturally redirects to /login.
const AUTH_FALLBACK_DEFAULTS: AuthContextType = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitializing: false,
  error: null,
  permissions: {},
  login: async () => {
    throw new Error('AuthContext unavailable — please reload the page');
  },
  logout: () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  },
  clearError: () => {},
  hasPermission: () => false,
};

// Hook สำหรับใช้บริบทการยืนยันตัวตน
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Context lost (Vite HMR re-evaluation or transient race) — log loudly but
    // do NOT throw, to keep ErrorBoundary out of this path. The safe defaults
    // make ProtectedRoute redirect to /login, which is the correct UX.
    if (typeof console !== 'undefined') {
      console.warn(
        '[useAuth] AuthContext unavailable — using safe defaults. ' +
        'This usually indicates a Vite HMR race condition. ' +
        'If seen in production, ensure <AuthProvider> wraps the component tree.'
      );
    }
    return AUTH_FALLBACK_DEFAULTS;
  }
  return context;
};

export default AuthContext;
